import { useEffect, useState } from 'react';
import axios from 'axios';

// ---------------------------------------------------------------------------
// Stats are read straight from the chain and the marketplace subgraph, not from
// a curated table, so the page can't drift from reality.
//
// Two things worth knowing about the sources:
//
//  1. The subgraph pages at 1000 records. Both `solds` and `auctions` already
//     exceed that, so every query here paginates — the app's own /auction
//     endpoint does not, which is why it reports exactly 1000 listings.
//  2. `Sold.totalPrice` carries BOTH currencies with no flag: MCRT is
//     9-decimals on BSC, BNB is 18. Summing them together would be meaningless,
//     so they are split on magnitude and reported as two separate volumes.
// ---------------------------------------------------------------------------

const SUBGRAPH =
  process.env.REACT_APP_SUBGRAPH_URL ||
  'https://api.studio.thegraph.com/query/90182/mc-marketplace/v0.0.1';

const GENESIS = (
  process.env.REACT_APP_GENESIS_CONTRACT_ADDRESS || ''
).toLowerCase();
const REVELATION = (
  process.env.REACT_APP_REVELATION_CONTRACT_ADDRESS || ''
).toLowerCase();

// A BNB-priced sale is ~1e17+; an MCRT-priced sale is ~1e15 or below.
const BNB_THRESHOLD = 1e16;
const PAGE = 1000;

async function queryAll(entity, fields) {
  const out = [];
  for (let skip = 0; skip < 6000; skip += PAGE) {
    // eslint-disable-next-line no-await-in-loop
    const res = await axios.post(SUBGRAPH, {
      query: `{ ${entity}(first: ${PAGE}, skip: ${skip}) { ${fields} } }`,
    });
    const rows = res?.data?.data?.[entity] || [];
    out.push(...rows);
    if (rows.length < PAGE) break;
  }
  return out;
}

// Supply is read straight from the token contracts over plain JSON-RPC, so the
// page works for a visitor with no wallet connected.
const RPC =
  process.env.REACT_APP_NODE_1 || 'https://bsc-dataseed1.binance.org';
const SEL_TOTAL_SUPPLY = '0x18160ddd'; // totalSupply()
const SEL_MAX_SUPPLY = '0x32cb6b0c'; // MAX_SUPPLY()

async function callUint(address, selector) {
  if (!address) return 0;
  const res = await axios.post(RPC, {
    jsonrpc: '2.0',
    id: 1,
    method: 'eth_call',
    params: [{ to: address, data: selector }, 'latest'],
  });
  const hex = res?.data?.result;
  if (!hex || hex === '0x') return 0;
  return parseInt(hex, 16) || 0;
}

const EMPTY = {
  loading: true,
  error: null,
  mintedGenesis: 0,
  mintedRevelation: 0,
  maxGenesis: 0,
  maxRevelation: 0,
  totalSales: 0,
  mcrtVolume: 0,
  bnbVolume: 0,
  uniqueTraders: 0,
  buyers: 0,
  sellers: 0,
  nftsTraded: 0,
  mostTraded: 0,
  biggestMcrtSale: 0,
  biggestBnbSale: 0,
  firstSaleDate: null,
  activeListings: 0,
  activeSellers: 0,
  listingsGenesis: 0,
  listingsRevelation: 0,
  holdersGenesis: 0,
  holdersRevelation: 0,
  cumulative: [],
};

export default function useNftStats() {
  const [stats, setStats] = useState(EMPTY);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [solds, auctions] = await Promise.all([
          queryAll('solds', 'contractAddress tokenId seller winner totalPrice soldAt'),
          queryAll('auctions', 'contractAddress seller isMCRT'),
        ]);

        const mcrtSales = solds.filter((s) => Number(s.totalPrice) < BNB_THRESHOLD);
        const bnbSales = solds.filter((s) => Number(s.totalPrice) >= BNB_THRESHOLD);

        const sum = (arr, d) =>
          arr.reduce((acc, s) => acc + Number(s.totalPrice) / d, 0);

        const buyers = new Set(solds.map((s) => s.winner));
        const sellers = new Set(solds.map((s) => s.seller));

        const perToken = {};
        solds.forEach((s) => {
          perToken[s.tokenId] = (perToken[s.tokenId] || 0) + 1;
        });

        // Cumulative sales by month — the honest shape of "how much has traded
        // here in total", and the one series that only ever grows.
        const byMonth = {};
        solds.forEach((s) => {
          const d = new Date(Number(s.soldAt) * 1000);
          const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
          if (!byMonth[key]) byMonth[key] = { genesis: 0, revelation: 0 };
          if (s.contractAddress === REVELATION) byMonth[key].revelation += 1;
          else byMonth[key].genesis += 1;
        });

        let cg = 0;
        let cr = 0;
        const cumulative = Object.keys(byMonth)
          .sort()
          .map((key) => {
            cg += byMonth[key].genesis;
            cr += byMonth[key].revelation;
            return { month: key, genesis: cg, revelation: cr, total: cg + cr };
          });

        const times = solds.map((s) => Number(s.soldAt)).filter(Boolean);

        // Holder counts come from the marketplace API. NOTE: that endpoint
        // matches contractAddress case-sensitively against lower-cased rows, so
        // it silently returns 0 unless the address is lower-cased first.
        const holderCount = async (addr) => {
          try {
            const r = await axios.get(
              `${process.env.REACT_APP_MARKETPLACE_BACK_URL}/nft-holder-count?contractAddress=${addr}`,
            );
            return Number(r?.data?.count) || 0;
          } catch (e) {
            return 0;
          }
        };

        const [hg, hr, mg, mr, xg, xr] = await Promise.all([
          holderCount(GENESIS),
          holderCount(REVELATION),
          callUint(GENESIS, SEL_TOTAL_SUPPLY),
          callUint(REVELATION, SEL_TOTAL_SUPPLY),
          callUint(GENESIS, SEL_MAX_SUPPLY),
          callUint(REVELATION, SEL_MAX_SUPPLY),
        ]);

        if (cancelled) return;

        setStats({
          loading: false,
          error: null,
          mintedGenesis: mg,
          mintedRevelation: mr,
          maxGenesis: xg,
          maxRevelation: xr,
          totalSales: solds.length,
          mcrtVolume: sum(mcrtSales, 1e9),
          bnbVolume: sum(bnbSales, 1e18),
          uniqueTraders: new Set([...buyers, ...sellers]).size,
          buyers: buyers.size,
          sellers: sellers.size,
          nftsTraded: Object.keys(perToken).length,
          mostTraded: Math.max(0, ...Object.values(perToken)),
          biggestMcrtSale: mcrtSales.length
            ? Math.max(...mcrtSales.map((s) => Number(s.totalPrice))) / 1e9
            : 0,
          biggestBnbSale: bnbSales.length
            ? Math.max(...bnbSales.map((s) => Number(s.totalPrice))) / 1e18
            : 0,
          firstSaleDate: times.length ? new Date(Math.min(...times) * 1000) : null,
          activeListings: auctions.length,
          activeSellers: new Set(auctions.map((a) => a.seller)).size,
          listingsGenesis: auctions.filter((a) => a.contractAddress === GENESIS).length,
          listingsRevelation: auctions.filter((a) => a.contractAddress === REVELATION)
            .length,
          holdersGenesis: hg,
          holdersRevelation: hr,
          cumulative,
        });
      } catch (e) {
        if (!cancelled) {
          setStats((s) => ({ ...s, loading: false, error: e?.message || 'error' }));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return stats;
}
